/**
 * Validation details panel — extracted from AdminQuestionEngineTab.
 * Renders academic + didactic validation results for a single question.
 */
import React from 'react';
import { Badge } from '@/components/ui/badge';
import {
  BookOpen, GraduationCap, AlertOctagon, CheckCircle,
  AlertTriangle, Lightbulb, RotateCcw,
} from 'lucide-react';

const ValidationDetailsPanel = ({ details, getStatusBadge }) => {
  if (!details) return null;
  const { academic, didactic } = details.validation_details || {};

  return (
    <div className="mt-4 pt-4 border-t space-y-4">
      {/* Academic Validation Section */}
      <div className="p-3 rounded-lg bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800">
        <div className="flex items-center gap-2 mb-3">
          <BookOpen className="w-4 h-4 text-blue-600" />
          <h4 className="font-semibold text-sm text-blue-800 dark:text-blue-200">
            Akademische Validierung
          </h4>
          {academic?.result && getStatusBadge(academic.result)}
        </div>

        {academic ? (
          <div className="space-y-2 text-sm">
            {academic.validated_difficulty && (
              <div className="flex items-center gap-2">
                <span className="text-gray-600 dark:text-gray-400">Validierte Schwierigkeit:</span>
                <Badge variant="outline" className="font-mono">{academic.validated_difficulty}</Badge>
              </div>
            )}

            {academic.normative_errors?.length > 0 && (
              <div className="mt-2">
                <div className="flex items-center gap-1 text-red-600 dark:text-red-400 font-medium">
                  <AlertOctagon className="w-3 h-3" />
                  <span>Normative Fehler:</span>
                </div>
                <ul className="mt-1 ml-4 list-disc text-xs text-red-700 dark:text-red-300 space-y-1">
                  {academic.normative_errors.map((error, idx) => (
                    <li key={`norm-${idx}-${(error || '').slice(0, 20)}`}>{error}</li>
                  ))}
                </ul>
              </div>
            )}

            {academic.justification_errors?.length > 0 && (
              <div className="mt-2">
                <span className="text-orange-600 dark:text-orange-400 font-medium text-xs">Begründungsfehler:</span>
                <ul className="mt-1 ml-4 list-disc text-xs text-orange-700 dark:text-orange-300">
                  {academic.justification_errors.map((error, idx) => (
                    <li key={`just-${idx}-${(error || '').slice(0, 20)}`}>{error}</li>
                  ))}
                </ul>
              </div>
            )}

            {academic.result === 'approved'
              && (!academic.normative_errors || academic.normative_errors.length === 0) && (
              <div className="flex items-center gap-2 text-green-600 dark:text-green-400">
                <CheckCircle className="w-4 h-4" />
                <span>Alle normativen Prüfungen bestanden</span>
              </div>
            )}

            <div className="text-xs text-gray-500 mt-2">
              Modell: {academic.model || 'N/A'} | {academic.timestamp ? new Date(academic.timestamp).toLocaleString('de-DE') : 'N/A'}
            </div>
          </div>
        ) : (
          <p className="text-sm text-gray-500">Keine akademischen Validierungsdaten verfügbar</p>
        )}
      </div>

      {/* Didactic Validation Section */}
      <div className="p-3 rounded-lg bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-800">
        <div className="flex items-center gap-2 mb-3">
          <GraduationCap className="w-4 h-4 text-purple-600" />
          <h4 className="font-semibold text-sm text-purple-800 dark:text-purple-200">
            Didaktische Evaluierung
          </h4>
          {didactic?.result && getStatusBadge(didactic.result)}
          {didactic?.regeneration_required && (
            <Badge className="bg-amber-500 text-white text-xs">
              <RotateCcw className="w-3 h-3 mr-1" />
              Regenerierung erforderlich
            </Badge>
          )}
        </div>

        {didactic ? (
          <div className="space-y-2 text-sm">
            <div className="flex flex-wrap gap-4">
              {didactic.cognitive_level_assessment && (
                <div className="flex items-center gap-2">
                  <span className="text-gray-600 dark:text-gray-400">Kognitives Level:</span>
                  <Badge variant="outline" className="font-mono bg-purple-100 dark:bg-purple-800">
                    {didactic.cognitive_level_assessment}
                  </Badge>
                </div>
              )}
              {didactic.difficulty_assessment && (
                <div className="flex items-center gap-2">
                  <span className="text-gray-600 dark:text-gray-400">Schwierigkeit:</span>
                  <Badge variant="outline" className="font-mono">{didactic.difficulty_assessment}</Badge>
                </div>
              )}
            </div>

            {didactic.identified_weaknesses?.length > 0 && (
              <div className="mt-3 p-2 rounded bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700">
                <div className="flex items-center gap-1 text-amber-700 dark:text-amber-300 font-medium text-xs mb-1">
                  <AlertTriangle className="w-3 h-3" />
                  <span>Identifizierte Schwächen:</span>
                </div>
                <ul className="ml-4 list-disc text-xs text-amber-800 dark:text-amber-200 space-y-1">
                  {didactic.identified_weaknesses.map((w, idx) => (
                    <li key={`weak-${idx}-${(w || '').slice(0, 20)}`}>{w}</li>
                  ))}
                </ul>
              </div>
            )}

            {didactic.improvement_recommendations?.length > 0 && (
              <div className="mt-3 p-2 rounded bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700">
                <div className="flex items-center gap-1 text-blue-700 dark:text-blue-300 font-medium text-xs mb-1">
                  <Lightbulb className="w-3 h-3" />
                  <span>Verbesserungsempfehlungen:</span>
                </div>
                <ul className="ml-4 list-disc text-xs text-blue-800 dark:text-blue-200 space-y-1">
                  {didactic.improvement_recommendations.map((rec, idx) => (
                    <li key={`rec-${idx}-${(rec || '').slice(0, 20)}`}>{rec}</li>
                  ))}
                </ul>
              </div>
            )}

            {didactic.result === 'approved'
              && (!didactic.identified_weaknesses || didactic.identified_weaknesses.length === 0) && (
              <div className="flex items-center gap-2 text-green-600 dark:text-green-400">
                <CheckCircle className="w-4 h-4" />
                <span>Alle didaktischen Qualitätsprüfungen bestanden</span>
              </div>
            )}

            <div className="text-xs text-gray-500 mt-2">
              Modell: {didactic.model || 'N/A'} | {didactic.timestamp ? new Date(didactic.timestamp).toLocaleString('de-DE') : 'N/A'}
            </div>
          </div>
        ) : (
          <p className="text-sm text-gray-500">Keine didaktischen Validierungsdaten verfügbar</p>
        )}
      </div>
    </div>
  );
};

export default ValidationDetailsPanel;
